import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";
import Logo from "@/components/Logo";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Reset link is missing or invalid.");
    }
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      // Backend auto-logs the user in by setting the httpOnly access_token
      // cookie on this same response. No JWT is exposed to JavaScript.
      setUser(data);
      toast.success("Password updated. You're signed in.");
      const target = data.role === "admin" ? "/admin" : data.role === "filmmaker" ? "/filmmaker" : "/browse";
      navigate(target, { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink grain flex items-center justify-center px-4 py-16">
      <div className="absolute top-6 left-6">
        <Link to="/" className="flex items-center group">
          <Logo size={32} />
        </Link>
      </div>

      <div className="w-full max-w-md bg-surface border border-white/10 rounded-2xl p-8 sm:p-10 shadow-2xl shadow-terracotta/5" data-testid="reset-card">
        <div className="mb-8">
          <div className="w-12 h-12 rounded-full bg-terracotta/15 text-terracotta flex items-center justify-center mb-4">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <h1 className="font-display text-3xl font-black mb-2">Set a new password</h1>
          <p className="text-foreground/60 text-sm">Choose a password you haven't used before. We'll sign you in right after.</p>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <div>
            <Label htmlFor="password" className="text-foreground/80 text-sm">New password</Label>
            <div className="relative mt-1.5">
              <Input
                id="password"
                type={show ? "text" : "password"}
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-ink border-white/10 h-11 pr-11"
                data-testid="reset-password-input"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/55 hover:text-foreground"
                aria-label={show ? "Hide password" : "Show password"}
                data-testid="reset-toggle-show"
              >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-foreground/45 mt-1">Minimum 8 characters.</p>
          </div>

          <div>
            <Label htmlFor="confirm" className="text-foreground/80 text-sm">Confirm new password</Label>
            <Input
              id="confirm"
              type={show ? "text" : "password"}
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1.5 bg-ink border-white/10 h-11"
              data-testid="reset-confirm-input"
            />
          </div>

          <Button
            type="submit"
            disabled={loading || !token}
            className="w-full bg-terracotta hover:bg-ember text-white h-11"
            data-testid="reset-submit-btn"
          >
            {loading ? "Updating…" : "Update password"}
          </Button>
        </form>

        <p className="mt-6 text-sm text-center text-foreground/60">
          <Link to="/login" className="text-terracotta hover:text-ember" data-testid="link-back-to-login">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
